import { store } from '@chilipiper/utils'
import {
  captureException,
  environment,
  initSentry,
  type ChiliDeanonInterface,
  type DebugMode,
  type Product,
  type ProductEvent,
  type TrackingEventCallback,
  type TrackingEventName,
  type TrackingMode,
  type TrackingTraits,
} from '@chilipiper/web-data-external'
import { CHILIPIPER_DEANON_AND_TRACKING_INTEGRATION_EVENT } from '../../shared/constants'
import { getEnv, loadEnvConfig } from '../../shared/env'
import type {
  DeanonTrackingEvent,
  DeanonymizationProvider,
  ProviderToggle,
  WebDataEvent,
} from '../../shared/types'
import { startConsentMonitor } from '../../shared/consentSignals'
import { CHILI_DEANON_DEBUG_KEY, logger } from './logger'
import { subscribeToSnippetsDeanonymizationEvents } from './providers'
import {
  subscribeToApiDeanonymizationEvents,
  subscribeToDeanonymizationSubmitted,
} from './providers/api'
import { buildIframe } from './iframe'
import { createEnqueuedSender } from './eventQueue'
import { createDeanonUpdateNotifier, type DeanonUpdateCallback } from './deanonUpdateNotifier'
import { createConsentGate, type ConsentGate } from './consentGate'

type InitParams = {
  tenantSubdomain: string
  tenantId: string
  mode?: TrackingMode
}

type DeanonResult = {
  guestId: string
}

const emitTrackingEvent = (event: DeanonTrackingEvent): void => {
  window.postMessage(
    {
      type: CHILIPIPER_DEANON_AND_TRACKING_INTEGRATION_EVENT,
      data: JSON.stringify(event),
    },
    '*'
  )
}

class ChiliDeanon implements ChiliDeanonInterface {
  #isLoading = true
  #isError = false
  #isInitialized = false
  #configuredProviders: ProviderToggle[] = []
  #installedProviders: DeanonymizationProvider[] = []
  #submittedProviders: DeanonymizationProvider[] = []
  #sendEventToIframe: ((event: WebDataEvent) => void) | null = null
  #guestId: string | null = null
  #deanonUpdateNotifier = createDeanonUpdateNotifier(captureException)
  #consentGate: ConsentGate | null = null

  setDebug(mode: DebugMode): void {
    store.set(CHILI_DEANON_DEBUG_KEY, mode)
    this.#sendEventToIframe?.({ type: 'Set_Debug_Mode', mode })
  }

  async init({ tenantId, tenantSubdomain }: InitParams): Promise<void> {
    if (this.#isInitialized) {
      logger.warn(
        'ChiliDeanon.init called twice — ignoring. Reload the page to re-initialize with different configuration.'
      )
      return
    }
    this.#isInitialized = true

    const envLoaded = await loadEnvConfig(`${environment.baseUrl}/web-data/env-config.js`, e =>
      logger.error('Error loading env file:', e)
    )
    if (!envLoaded) return

    initSentry()

    if (!tenantId) {
      logger.error('tenantId is required')
      this.#isError = true
      return
    }

    if (getEnv('DEPLOY_ENV') !== 'development' && !tenantSubdomain) {
      logger.error('tenantSubdomain is required')
      this.#isError = true
      return
    }

    // Partner sites may include the snippet synchronously in <head>, in which case
    // document.body is not yet available when init() first runs. Wait for DOMContentLoaded
    // before building the iframe to avoid a body.appendChild crash.
    if (!document.body) {
      await new Promise<void>(resolve => {
        document.addEventListener('DOMContentLoaded', () => resolve(), { once: true })
      })
    }

    logger.log('Initializing ChiliDeanon')
    try {
      // The visitor's consent is read here, on the host page, where the signals live: before the
      // iframe exists, so it knows the initial state from its URL, then as Set_Consent on every
      // change. The decision — whether this tenant applies it — is made in the iframe and comes
      // back as Consent_Decision; the gate below carries it out.
      const consentGate = createConsentGate({ log: message => logger.log(message) })
      this.#consentGate = consentGate

      let consentDenied = false
      startConsentMonitor(
        denied => {
          consentDenied = denied
          try {
            this.#sendEventToIframe?.({ type: 'Set_Consent', denied })
          } catch (error) {
            logger.warn('Could not send the consent state to the iframe', error)
          }
        },
        message => logger.log(message)
      )

      const { sendEvent, initPromise, analyticsSettingsPromise, onConsentDecision } = buildIframe(
        tenantId,
        { consentDenied }
      )
      this.#sendEventToIframe = sendEvent
      onConsentDecision(enforce => consentGate.setDecision(enforce))

      const { enqueueEvent, start } = createEnqueuedSender(sendEvent)

      const guestId = await initPromise.catch(error => {
        const errorMessage = `Failed to initialize ChiliDeanon iframe: ${error.message}`
        if (getEnv('DEPLOY_ENV') === 'development') {
          logger.warn(errorMessage)
        } else {
          logger.error(errorMessage)
        }
        this.#isError = true
      })

      if (this.#isError || !guestId) {
        return
      }

      this.#guestId = guestId

      // The iframe has its listener now. A consent change while it was loading was posted to a
      // window without a listener, so the current state is sent again.
      try {
        sendEvent({ type: 'Set_Consent', denied: consentDenied })
      } catch (error) {
        logger.warn('Could not send the consent state to the iframe', error)
      }

      // Emit Identify event immediately — tracking doesn't need to wait for providers
      emitTrackingEvent({ type: 'Identify', guestId })

      await analyticsSettingsPromise
        .then(settings => {
          try {
            this.#configuredProviders =
              settings?.deanonymizationProviders?.flatMap(provider =>
                provider.enabled ? [provider] : []
              ) ?? []
            this.#isLoading = false
          } catch (error) {
            captureException(error)
            this.#isError = true
          }
        })
        .catch(error => {
          logger.error(`Failed to load deanonymization settings: ${error.message}`)
          this.#isError = true
        })

      if (this.#isError) {
        return
      }

      const onDeanonymizationEvent: Parameters<
        typeof subscribeToSnippetsDeanonymizationEvents
      >[0]['onEvent'] = event => {
        if (event.type === 'Deanonymization_Provider_Ready') {
          const provider = this.#configuredProviders.find(
            item => item.provider === event.provider || item.poweredBy === event.provider
          )
          if (provider) {
            this.#installedProviders.push(provider.poweredBy ?? provider.provider)
          }
          return
        }

        if (event.type === 'Deanonymization') {
          if (!consentGate.allowsBatch()) {
            return
          }
          // Emit each deanon batch to tracking as it resolves
          emitTrackingEvent({ type: 'Deanonymization', data: event.data })
        }

        try {
          enqueueEvent(event)
        } catch (error) {
          captureException(error)
        }
      }

      // Starts once the iframe's decision allows it; until then nothing is read. A decision to
      // enforce stops it — the provider reads end and the listeners come off — and a later decision
      // to proceed starts it again.
      consentGate.collectionReady(() => {
        const collection = new AbortController()

        subscribeToSnippetsDeanonymizationEvents({
          providers: this.#configuredProviders,
          onEvent: onDeanonymizationEvent,
          signal: collection.signal,
        })

        subscribeToApiDeanonymizationEvents({
          onEvent: onDeanonymizationEvent,
          signal: collection.signal,
        })

        return () => collection.abort()
      })

      // A provider is "submitted" once its values are persisted to the BE. deanonymize()
      // waits on this before resolving, and onDeanonUpdate triggers from it — so consumers
      // always act on a BE that already has the new data.
      subscribeToDeanonymizationSubmitted({
        onSubmitted: data => {
          data.forEach(item => {
            const itemProvider = item.provider === 'ChiliPiper' ? item.poweredBy : item.provider
            if (!this.#submittedProviders.includes(itemProvider)) {
              this.#submittedProviders.push(itemProvider)
            }
          })
          this.#deanonUpdateNotifier.handle(data)
        },
      })

      start()
      logger.log('ChiliDeanon initialized')
    } catch (error) {
      captureException(error)
    }
  }

  async deanonymize(): Promise<DeanonResult | undefined> {
    if (!this.#guestId) {
      logger.error('ChiliDeanon not initialized — call init() first')
      return
    }

    let providersReadyByTimeout: DeanonymizationProvider[] = []

    // race 2s timeout vs detecting all configured providers as installed
    await new Promise<void>(resolve => {
      let timedout = false
      const timeoutId = setTimeout(() => {
        timedout = true
      }, 2_000)

      const id = setInterval(() => {
        if (this.#isError) {
          clearInterval(id)
          clearTimeout(timeoutId)
          resolve()
        }

        // A denied visitor has no providers to wait for: selection runs without identification.
        if (this.#consentGate?.isEnforced()) {
          clearInterval(id)
          clearTimeout(timeoutId)
          resolve()
          return
        }

        if (this.#isLoading) {
          return
        }

        if (!this.#configuredProviders.length) {
          clearInterval(id)
          clearTimeout(timeoutId)
          resolve()
        }

        if (timedout) {
          providersReadyByTimeout = [...this.#installedProviders]
          clearInterval(id)
          resolve()
          logger.error(
            `Timedout waiting for providers to load. Loaded ${JSON.stringify(providersReadyByTimeout)} but configured was ${JSON.stringify(this.#configuredProviders)}`
          )
          return
        }

        if (
          this.#configuredProviders.every(({ provider, poweredBy }) =>
            provider === 'ChiliPiper'
              ? poweredBy && this.#installedProviders.includes(poweredBy)
              : this.#installedProviders.includes(provider)
          )
        ) {
          providersReadyByTimeout = [...this.#installedProviders]
          clearInterval(id)
          clearTimeout(timeoutId)
          resolve()
        }
      }, 100)
    })

    // race 6s timeout vs all providers' values being persisted to the BE. We wait for the
    // BE write (not just the provider emitting) so the guestId we return points at a BE that
    // already has the initial deanon data — otherwise a consumer's first query could beat it.
    await new Promise<void>(resolve => {
      let timedout = false
      const timeoutId = setTimeout(() => {
        timedout = true
      }, 6_000)

      const id = setInterval(() => {
        if (timedout) {
          clearInterval(id)
          resolve()
          return
        }

        if (
          providersReadyByTimeout.every(provider => this.#submittedProviders.includes(provider))
        ) {
          clearInterval(id)
          clearTimeout(timeoutId)
          resolve()
        }
      }, 100)
    })

    // The initial deanon data is now persisted; from here, provider changes are updates.
    this.#deanonUpdateNotifier.markReady()

    return { guestId: this.#guestId }
  }

  /**
   * Registers a trigger fired whenever deanonymized values change (e.g. LiftAi raising a visitor's
   * intent mid-session). It fires after the updated values have been persisted to the BE and only
   * when a value actually changed — so a consumer reacting to it (e.g. re-running playbook
   * selection) queries a BE that already has the new data. The callback receives no value. Returns
   * an unsubscribe function. Backwards-compatible — existing deanonymize() callers are unaffected.
   */
  onDeanonUpdate(callback: DeanonUpdateCallback): () => void {
    return this.#deanonUpdateNotifier.subscribe(callback)
  }
}

logger.info(`v${__APP_VERSION__} loaded`)

// Window.ChiliDeanon / Window.ChiliTracking are declared in @chilipiper/web-data-external —
// they are the contract external consumers (conciergejs, chat embed) program against.
declare global {
  interface Window {
    ChiliWebData?: {
      init: (params: InitParams) => Promise<void>
      waitForDeanonymization: (cb: () => void) => Promise<void>
      productImpression: (product: string) => void
      productEvent: <P extends Product>(product: P, event: ProductEvent<P>) => void
      page: () => void
      identify: (traits: TrackingTraits) => void
      on: (event: TrackingEventName | '*', callback: TrackingEventCallback) => () => void
      off: (event: TrackingEventName | '*', callback: TrackingEventCallback) => void
      enableTracking: () => void
      disableTracking: () => void
      setDebug: (mode: DebugMode) => void
      onDeanonUpdate: (callback: DeanonUpdateCallback) => () => void
    }
  }
}

const deanon = new ChiliDeanon()
window.ChiliDeanon = deanon

// Backwards compat wrapper for existing code using window.ChiliWebData
window.ChiliWebData = {
  init: async params => {
    await window.ChiliTracking?.init({
      tenantId: params.tenantId,
      mode: params.mode,
    })
    await deanon.init(params)
  },
  waitForDeanonymization: async cb => {
    await deanon.deanonymize()
    cb()
  },
  productImpression: product => window.ChiliTracking?.productImpression(product),
  productEvent: (product, event) => window.ChiliTracking?.productEvent(product, event),
  page: () => window.ChiliTracking?.page(),
  identify: traits => window.ChiliTracking?.identify(traits),
  on: (event, callback) => window.ChiliTracking?.on(event, callback) ?? (() => {}),
  off: (event, callback) => window.ChiliTracking?.off(event, callback),
  enableTracking: () => window.ChiliTracking?.enableTracking(),
  disableTracking: () => window.ChiliTracking?.disableTracking(),
  setDebug: mode => {
    window.ChiliTracking?.setDebug(mode)
    deanon.setDebug(mode)
  },
  onDeanonUpdate: callback => deanon.onDeanonUpdate(callback),
}
